import { useState, useEffect } from "react";
import { Building2 } from "lucide-react";
import {
  getMembershipsWithPermissions,
  getCurrentOrgId,
  setCurrentOrg,
  subscribeToOrgContext,
} from "../../../state/orgContext";
import { cn } from "../../utils/cn";

const ORG_TYPE_LABELS: Record<string, string> = {
  xlkey_internal: "XLKey",
  ambassador_org: "Ambassadeur",
  client_org: "Client",
  partner_org: "Partenaire",
  farm: "Exploitation",
  drone_pilot_org: "Pilote",
};

export function OrgSwitcher() {
  const [memberships, setMemberships] = useState(getMembershipsWithPermissions());
  const [currentId, setCurrentId] = useState<string | null>(getCurrentOrgId());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setMemberships(getMembershipsWithPermissions());
    setCurrentId(getCurrentOrgId());
    return subscribeToOrgContext(() => {
      setMemberships(getMembershipsWithPermissions());
      setCurrentId(getCurrentOrgId());
    });
  }, []);

  if (memberships.length <= 1) return null;

  const current = memberships.find((m) => m.org_id === currentId);
  const label = current
    ? `${ORG_TYPE_LABELS[current.org_type] ?? current.org_type} (${current.org_id.slice(0, 8)}…)`
    : "Organisation";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-left text-sm text-text shadow-sm hover:bg-surface-muted",
          open && "ring-2 ring-accent"
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="max-w-[140px] truncate">{label}</span>
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <ul
            className="absolute right-0 top-full z-50 mt-1 max-h-64 w-56 overflow-auto rounded-md border border-border bg-surface py-1 shadow-lg"
            role="listbox"
          >
            {memberships.map((m) => (
              <li key={m.org_id} role="option">
                <button
                  type="button"
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm hover:bg-surface-muted",
                    m.org_id === currentId && "bg-surface-muted font-medium"
                  )}
                  onClick={() => {
                    setCurrentOrg(m.org_id);
                    setOpen(false);
                  }}
                >
                  <span className="block truncate">
                    {ORG_TYPE_LABELS[m.org_type] ?? m.org_type}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {m.org_id}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
